import { useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { Button, Dialog, FormSection, HelpList, Icon, More, formatDate, formatRelative } from "@omnabase/ui";
import { PUBLIC_API_SCOPES, PUBLIC_API_SCOPE_LABELS, siteFromInput, type PublicApiScope, type Site } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useSetParam, useWs } from "../../lib/nav";
import { capsOf } from "../../lib/access";
import { keyProblem, siteNameOf, sites, type SitesList } from "../../lib/sites";
import { useCopy } from "./ConnectedAi";

/** Settings › Integrations › API keys (M12 round one; brief `docs/briefs/m12-public-api.md`, choice 1): the workspace's connected sites
 *  and scripts, each with its one live key (its last four characters, what it may do, where), "New key" and Revoke. Owners and
 *  administrators only (workspace-wide project settings); everyone else does not see the section. The key is shown once, in the dialog
 *  after Create key, and kept nowhere. Open states live in the URL: `?apikey=new`, `?apikey=revoke-<site id>`, `?apikey=details-<site id>`.
 *  M14 round three (choices 9, 10): a key that came through Connect carries the pill "Connected site"; revoking a connected site's key is
 *  "Disconnect {host/folder}?"; a More points scripts to the developer page. M14 round six (the founder): the table is Site, Key, Projects,
 *  Details, Revoke; the rest ("connected {date} · by {name}" for a Connect key, the last connect, "{date} · by {name}" for a hand-made one,
 *  what it may do, the last use) is in the row's Details dialog "Key of {site}". Integrations round two (the founder, 2026-10-13: nothing
 *  site-made under "For developers"): the table lists only hand-made keys; a connected site's key shows on its WordPress row, whose menu opens
 *  this section's Details and Disconnect dialogs, so both look the site up among all sites. Round three (2026-10-13): the API keys tab, shown
 *  only with a hand-made key (`shown` draws the section; the dialogs render on any tab: "Create key" on Add new, the WordPress row's Key
 *  details and Disconnect); a new key selects the tab behind "Copy your key now". */
const KEY_RULES = ["Shown once, when it is made", "Sent with every call as Authorization: Bearer and the key", "Revoke stops it at once", "Keys for sites and scripts; a member's own-AI key is under Your own AI"];
/** M14 round three (choice 9; review B4, N8): what revoking a connected site's key does, as the Disconnect dialog lists it */
const DISCONNECT_LINES = ["Its key stops at once", "Its addresses are removed", "The site's settings page says 'Disconnected in OmnaBase' until someone clicks Connect there"];

/** the sites list is the page's (`IntegrationsSettings`): the WordPress card reads the same rows and opens this section's dialogs */
export function ApiKeysSettings({ list, shown }: { list: SitesList; shown: boolean }) {
  const { workspace } = useSession();
  const [params] = useSearchParams(); const setParam = useSetParam();
  const admin = capsOf(workspace).has("projectSettings");
  if (!admin) return null;
  const param = params.get("apikey") ?? "";
  const all = list.data?.rows ?? [];
  const rows = all.filter((s) => !s.connected); // a connected site's key is shown on its WordPress row
  const revoking = param.startsWith("revoke-") ? all.find((s) => s.id === param.slice(7)) : undefined;
  const detailed = param.startsWith("details-") ? all.find((s) => s.id === param.slice(8)) : undefined;
  const close = () => setParam("apikey", null);
  const added = () => { void list.reload(); setParam("tab", "api-keys"); };
  return (
    <>
      {shown && <FormSection title="API keys" audience="admins" intro={<>The keys your own scripts and systems call this workspace&apos;s API with; each belongs to the workspace, not to the person who made it.<HelpList items={KEY_RULES} /><More label="For scripts of your own"><p>The API guide lists every call, its fields and its answers: <a className="ob-link" href="/developers/api" target="_blank" rel="noreferrer">the developer page</a>.</p></More></>}>
        {rows.length > 0 && <SitesTable rows={rows} onDetails={(s) => setParam("apikey", `details-${s.id}`)} onRevoke={(s) => setParam("apikey", `revoke-${s.id}`)} />}
        <div className="ob-form__foot"><Button icon="plus" onClick={() => setParam("apikey", "new")}>New key</Button></div>
      </FormSection>}
      {detailed && <KeyDetailsDialog site={detailed} onClose={close} />}
      {param === "new" && <NewKeyDialog onClose={close} onCreated={added} />}
      {revoking && <RevokeDialog site={revoking} onClose={close} onDone={() => void list.reload()} />}
    </>
  );
}

/** Revoke a hand-made key, or Disconnect a connected site (its key is what goes); the dialog stays open, busy, until the server answers */
function RevokeDialog({ site: s, onClose, onDone }: { site: Site; onClose: () => void; onDone: () => void }) {
  const { slug } = useWs(); const { toast } = useSession();
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    setBusy(true);
    try { await sites.revoke(slug, s.id); toast(s.connected ? `${siteNameOf(s)} disconnected` : `Key of ${s.name} revoked`, { kind: "success" }); onClose(); onDone(); }
    catch (e) { toast((e as Error).message, { kind: "error" }); setBusy(false); }
  };
  if (s.connected) return (
    <Dialog title={`Disconnect ${siteNameOf(s)}?`} intro="It can be connected again from the site at any time." onClose={onClose}
      footer={<><Button onClick={onClose}>Keep it</Button><Button variant="primary" busy={busy} onClick={() => void revoke()}>Disconnect</Button></>}>
      <ul className="ob-consequences">{DISCONNECT_LINES.map((t) => <li key={t}><Icon name="alarm" size="sm" />{t}</li>)}</ul>
    </Dialog>
  );
  return (
    <Dialog title={`Revoke the key of ${s.name}?`} intro={`Ending in ${s.keyLast4}. A new key can be made at any time.`} onClose={onClose}
      footer={<><Button onClick={onClose}>Keep it</Button><Button variant="primary" busy={busy} onClick={() => void revoke()}>Revoke</Button></>}>
      <ul className="ob-consequences"><li><Icon name="alarm" size="sm" />Calls with this key are refused at once.</li><li><Icon name="alarm" size="sm" />What it already sent stays as it is.</li></ul>
    </Dialog>
  );
}

/** M14 round five (the founder: "tables instead of card lists"), round six (the founder: "fewer columns"): Site, Key, Projects, Details and
 *  Revoke; the Key and Projects columns leave on a narrow card (the kit's container rules). Hand-made keys only (2026-10-13). */
function SitesTable({ rows, onDetails, onRevoke }: { rows: Site[]; onDetails: (s: Site) => void; onRevoke: (s: Site) => void }) {
  const projectNames = useProjectNames();
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-api-keys" aria-label="API keys">
        <thead><tr><th>Site</th><th className="ob-dtable__col--below-420">Key</th><th className="ob-dtable__col--below-540">Projects</th><th>Details</th><th></th></tr></thead>
        <tbody>{rows.map((s) => (
          <tr key={s.id}>
            <td className="ob-dtable__grow"><span className="ob-dtable__who"><span className="ob-dtable__name ob-api-keys__site" title={s.name}>{s.name}</span></span></td>
            <td className="ob-dtable__col--below-420"><code>obw_…{s.keyLast4}</code></td>
            <td className="ob-dtable__muted ob-dtable__col--text ob-dtable__col--below-540">{projectNames(s.projects)}</td>
            <td className="ob-dtable__actions ob-api-keys__details"><Button variant="ghost" size="sm" aria-label={`Details of ${s.name}`} onClick={() => onDetails(s)}>Details</Button></td>
            <td className="ob-dtable__actions"><Button variant="ghost" size="sm" aria-label={`Revoke ${s.name}`} onClick={() => onRevoke(s)}>Revoke…</Button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

/** the projects a key reaches, by name ("All projects" when it names none) */
function useProjectNames(): (ids: string[]) => string {
  const { workspace } = useSession();
  return (ids) => (ids.length ? ids.map((id) => workspace.projects.find((p) => p.id === id)?.name ?? "A removed project").join(", ") : "All projects");
}

/** M14 round six: every fact of one key, the ones the table no longer shows included; at `?apikey=details-<site id>` (a connected site's
 *  from its WordPress row's "Key details…", 2026-10-13) */
function KeyDetailsDialog({ site: s, onClose }: { site: Site; onClose: () => void }) {
  const { workspace } = useSession();
  const projectNames = useProjectNames();
  const maker = workspace.members.find((m) => m.userId === s.createdBy)?.name ?? "someone";
  const facts: [string, ReactNode][] = [
    ["Since", s.connected ? <>connected {formatDate(s.createdAt)} · by {maker}</> : <>{formatDate(s.createdAt)} · by {maker}</>],
    ["Key", <code key="k">obw_…{s.keyLast4}</code>],
    ["Projects", projectNames(s.projects)],
    ["It may", <ul key="m" className="ob-api-keys__scopes">{s.scopes.map((x) => <li key={x}>{PUBLIC_API_SCOPE_LABELS[x]}</li>)}</ul>],
    ["Last used", s.lastUsedAt ? formatRelative(s.lastUsedAt, Date.now()) : "Never"],
  ];
  return (
    <Dialog title={`Key of ${s.name}`} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <dl className="ob-api-keys__facts">{facts.map(([label, value]) => <div key={label} className="ob-api-keys__fact"><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    </Dialog>
  );
}

/** the site (an address or a name), what the key may do, where; then the key once */
function NewKeyDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { slug } = useWs(); const { workspace } = useSession();
  const [site, setSite] = useState(""); const [scopes, setScopes] = useState<PublicApiScope[]>([]); const [project, setProject] = useState("");
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const problem = keyProblem(site, scopes);
  const live = workspace.projects.filter((p) => !p.archivedAt);
  const create = async () => {
    if (problem || busy) return;
    setBusy(true); setError(null);
    try { const made = await sites.create(slug, { ...siteFromInput(site), scopes, projects: project ? [project] : [] }); setKey(made.key); onCreated(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };
  if (key) return <KeyShownDialog secret={key} onClose={onClose} />;
  const toggle = (s: PublicApiScope, on: boolean) => setScopes((cur) => (on ? PUBLIC_API_SCOPES.filter((x) => x === s || cur.includes(x)) : cur.filter((x) => x !== s)));
  return (
    <Dialog title="New key" intro="Name the site or script that uses it, what it may do and in which project." onClose={onClose}
      footer={<><span className="ob-dialog__foot-meta">{error ?? (site.trim() || scopes.length ? problem : "")}</span><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!!problem} onClick={() => void create()}>Create key</Button></>}>
      <label className="ob-dialog__label" htmlFor="apikey-site">Site</label>
      <input id="apikey-site" className="ob-dialog__input" autoFocus value={site} placeholder="forum.example.com, or a name: Status page" onChange={(e) => setSite(e.target.value)} />
      <span className="ob-dialog__label">It may</span>
      <ul className="ob-checks" aria-label="What the key may do">{PUBLIC_API_SCOPES.map((s) => (
        <li key={s}><span>{PUBLIC_API_SCOPE_LABELS[s]}<code>{s}</code></span><input type="checkbox" className="ob-check" aria-label={PUBLIC_API_SCOPE_LABELS[s]} checked={scopes.includes(s)} onChange={(e) => toggle(s, e.target.checked)} /></li>
      ))}</ul>
      <label className="ob-dialog__label" htmlFor="apikey-project">Project</label>
      <select id="apikey-project" className="ob-select" value={project} onChange={(e) => setProject(e.target.value)}>
        <option value="">All projects</option>
        {live.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <p className="ob-dialog__hint">{project ? "Other projects do not exist for this key." : "It reaches every project, today's and later ones."}</p>
    </Dialog>
  );
}

function KeyShownDialog({ secret, onClose }: { secret: string; onClose: () => void }) {
  const copy = useCopy();
  return (
    <Dialog title="Copy your key now" intro="Shown only this once; later the list shows its last four characters." width={600} onClose={onClose} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <div className="ob-secret"><code aria-label="The new key">{secret}</code><Button size="sm" icon="copy" onClick={() => copy(secret, "Key")}>Copy</Button></div>
      <HelpList items={["Keep it on the site's server, never in a page a visitor loads", "Send it as Authorization: Bearer and the key"]} />
    </Dialog>
  );
}
